import {
  AcAutocomplete,
  AcButton,
  AcCheckbox,
  AcContainer,
  AcFormCard,
  AcTable,
  AcTypography,
} from '@components'
import { ICONS, LABELS } from '@constants'
import { useStore } from '@hooks/use-store'
import { ITagRelatedProduct } from '@typings'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import styles from './tag-related-items.module.scss'

export interface ITagTargetedProductsTab {
  name?: string
}

export const TagTargetedProductsTab = observer(
  (_props: ITagTargetedProductsTab) => {
    const { tags, products: productsStore } = useStore()
    const [selectedProducts, setSelectedProducts] = useState<Set<number>>(
      new Set()
    )
    const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null)
    const [isSearching, setIsSearching] = useState(false)

    const products = tags.single?.targeted_products || []
    const linkedProductIds = new Set(products.map(p => p.id))

    // Filter search results to exclude already linked products
    const availableProducts = productsStore.search_results.filter(p => !linkedProductIds.has(p.id))

    const handleSearch = async (query: string) => {
      if (searchDebounce) clearTimeout(searchDebounce)

      if (!query) {
        setIsSearching(false)
        return // Don't query on empty input
      }

      setIsSearching(true)

      const timeout = setTimeout(async () => {
        await productsStore.searchProducts(query)
        setIsSearching(false)
      }, 300)

      setSearchDebounce(timeout)
    }

    const handleProductCheckbox = (productId: number) => {
      setSelectedProducts(prev => {
        const newSet = new Set(prev)
        if (newSet.has(productId)) {
          newSet.delete(productId)
        } else {
          newSet.add(productId)
        }
        return newSet
      })
    }

    const handleDetachProducts = async () => {
      if (selectedProducts.size === 0) return

      try {
        await tags.detachTargetedProducts(Array.from(selectedProducts))
        setSelectedProducts(new Set())
      } catch (e) {
        // Error already handled in store with toast notification
        // Prevent error from bubbling up to React error boundary
      }
    }

    const handleAttachProduct = async (product: any) => {
      if (!product?.id) return
      try {
        await tags.attachTargetedProducts([product.id])
        // Clear search results after attaching
        productsStore.search_results = []
      } catch (e) {
        // Error already handled in store with toast notification
        // Prevent error from bubbling up to React error boundary
      }
    }

    const handleSelectAllProducts = () => {
      if (selectedProducts.size === products.length) {
        setSelectedProducts(new Set())
      } else {
        setSelectedProducts(new Set(products.map(p => p.id)))
      }
    }

    return (
      <AcContainer>
        <AcFormCard title={LABELS.TARGETED_PRODUCTS}>
          <div className={styles['actions']}>
            <div className={styles['search-container']}>
              <AcAutocomplete
                name="targeted_product_select"
                id="targeted_product_select"
                options={availableProducts}
                valueKey="id"
                labelKey="name"
                loading={isSearching}
                placeholder={LABELS.SEARCH_PRODUCTS}
                onSearch={handleSearch}
                onChange={(product) => {
                  handleAttachProduct(product)
                }}
              />
            </div>
            {products.length > 0 && (
              <div className={styles['button-group']}>
                <AcButton
                  onClick={handleSelectAllProducts}
                  label={
                    selectedProducts.size === products.length
                      ? LABELS.CLEAR
                      : LABELS.SELECT_ALL_ROWS
                  }
                  color="neutral-700"
                />
                <AcButton
                  onClick={handleDetachProducts}
                  label={LABELS.DETACH_SELECTED}
                  icon={ICONS.DELETE}
                  color="secondary"
                  disabled={selectedProducts.size === 0}
                />
              </div>
            )}
          </div>

          {products.length > 0 ? (
            <AcTable>
              <thead>
                <tr>
                  <th></th>
                  <th>{LABELS.PRODUCT_NUMBER}</th>
                  <th>{LABELS.NAME}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product: ITagRelatedProduct) => (
                  <tr key={product.id}>
                    <td>
                      <AcCheckbox
                        label={`Select ${product.name}`}
                        checked={selectedProducts.has(product.id)}
                        onClick={() => handleProductCheckbox(product.id)}
                      />
                    </td>
                    <td>{product.product_number}</td>
                    <td>{product.name}</td>
                  </tr>
                ))}
              </tbody>
            </AcTable>
          ) : (
            <AcTypography>{LABELS.NO_TARGETED_PRODUCTS_LINKED}</AcTypography>
          )}
        </AcFormCard>
      </AcContainer>
    )
  }
)
